import * as React from 'react'
import { type ColumnDef } from '@tanstack/react-table'
import { useAppNavigate } from '@/hooks/useAppNavigate'
import {
  DataTable,
  sortableColumns,
  type ColumnDefWithLabel as BaseColumnDefWithLabel,
} from '@/components/DataTable'
import { useSessionTypesQuery } from '../hooks/useSessionTypesQuery'
import type { SessionTypeSummary, SessionTypesQueryParams } from '../types'

const UnavailableData = ({
  className = 'text-gray-400',
}: {
  className?: string
}) => <span className={className}>-</span>

type ColumnDefWithLabel = BaseColumnDefWithLabel<SessionTypeSummary>

const columnsWithLabels: ColumnDefWithLabel[] = [
  {
    accessorKey: 'slug',
    label: 'Type',
    cell: ({ row }) => {
      const sessionType = row.original.slug
      if (!sessionType) {
        return <UnavailableData />
      }
      // Format slug to readable name: "appt-booking" -> "Appt Booking"
      const formattedName = sessionType
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
      return (
        <div className="truncate max-w-xs hover:underline cursor-pointer">
          {formattedName}
        </div>
      )
    },
  },
  {
    accessorKey: 'total_sessions',
    label: 'Total Sessions',
    cell: ({ row }) => {
      const value = row.original.total_sessions ?? 0
      const formatted =
        typeof value === 'number'
          ? value.toLocaleString('en-US')
          : String(value)
      return <div className="whitespace-nowrap">{formatted}</div>
    },
  },
  {
    accessorKey: 'last_session_at',
    label: 'Last Session',
    cell: ({ row }) => {
      const raw = row.original.last_session_at
      if (!raw) {
        return <UnavailableData />
      }

      try {
        const date = new Date(raw)
        if (Number.isNaN(date.getTime())) {
          return <UnavailableData />
        }

        const formatted = date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })

        return <div className="whitespace-nowrap">{formatted}</div>
      } catch {
        return <UnavailableData />
      }
    },
  },
]

const tableColumns: ColumnDef<SessionTypeSummary>[] =
  sortableColumns(columnsWithLabels)

export function SessionTypesListView() {
  const { toTask } = useAppNavigate()
  const [queryParams, setQueryParams] = React.useState<SessionTypesQueryParams>(
    {
      limit: 50,
      sort_by: 'last_session_at',
      sort_order: 'desc',
    }
  )

  const queryResult = useSessionTypesQuery(queryParams)

  return (
    <DataTable
      showFilters={false}
      queryResult={queryResult}
      onQueryParamsChange={(params) =>
        setQueryParams(params as SessionTypesQueryParams)
      }
      columns={tableColumns}
      getRowKey={(row: SessionTypeSummary) => `${row.org}:${row.slug}`}
      refreshQueryKey={['session-types']}
      onRowClick={(row) => toTask(`/sessions/${row.slug}`)}
    />
  )
}
